import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmployerLayout } from '../JobPostPricing/EmployerLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/Components/ui/card';
import { Heading, Text } from '@/Components/ui/typography';
import { Badge } from '@/Components/ui/badge';
import { Button } from '@/Components/ui/button';
import { Textarea } from '@/Components/ui/textarea';
import { Label } from '@/Components/ui/label';
import { Alert, AlertDescription, AlertTitle, Progress } from '@/Components/ui/feedback';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/Components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Components/ui/tabs';
import { cn } from '@/lib/utils';
import { 
  Sparkles, Mic, FileEdit, Upload, Zap, ArrowRight, Loader2, 
  Lightbulb, Briefcase, Users, DollarSign, MapPin, Clock, 
  CheckCircle2, Copy, ChevronDown, MessageSquare, Volume2, Radio,
  Headphones, X, FileText, Palette, UserCheck, Calculator, Settings
} from 'lucide-react';
import { getColor } from '@/config/theme';
import { getApiUrlsWithFallback, getApiUrl } from '../../../config/api';
import axios from 'axios';
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import Recorder from "recorder-js";
import { toast } from 'sonner';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import creditServices from '@/Services/creditServices';
import CreditWidget from '@/HireWithUs/Components/creditWidget';

const API_KEY = process.env.REACT_APP_COG_SERVICE_KEY;
const API_LOCATION = process.env.REACT_APP_COG_SERVICE_LOCATION;
const speechConfig = API_KEY && API_LOCATION ? SpeechSDK.SpeechConfig.fromSubscription(API_KEY, API_LOCATION) : null;
const REMOTE_AUDIO_RMS_THRESHOLD = 0.012;
const REMOTE_AUDIO_SILENCE_DEBOUNCE_MS = 250;

// Voice AI questions
const questions = [
  "Hello, I hope you are doing well today. I would help you to create a job description. I will ask a few key questions to understand what you are looking for.",
  "What is the title of the role you are hiring for?",
  "What level of experience are you looking for in this role?",
  "Is there a salary range or budget you would like to define for this position?",
  "How would you describe this role to a potential candidate in a few sentences?",
  "What are the primary responsibilities associated with this position?",
  "Which skills or qualifications are essential for success in this role?",
  "Where will this role be based? (Remote, hybrid, or a specific location)",
  "Thank you. I have created the job description based on your responses. Please review and save it."
];

// Quick prompts for text AI
const quickPrompts = [
  { 
    icon: Briefcase, 
    label: "Tech Role", 
    description: "Engineering & Development",
    prompt: "We need a Senior Software Engineer with 5+ years experience in full-stack development, React, Node.js, and cloud technologies (AWS, Docker). The role is remote-first with flexible hours. Responsibilities include building scalable web applications, designing system architecture, code reviews, and mentoring junior developers. We're a fast-growing tech startup with a collaborative culture and focus on innovation. Budget is ₹12-18 lakhs." 
  },
  { 
    icon: Users, 
    label: "Management", 
    description: "Leadership & Strategy",
    prompt: "We need a Product Manager with 4-6 years experience in agile methodologies, stakeholder management, and data-driven decision making. The role is hybrid with 2-3 days in office. Responsibilities include product roadmap planning, cross-functional collaboration, user research, and driving product metrics. We're a mid-size SaaS company with a results-oriented culture. Budget is ₹15-22 lakhs." 
  },
  { 
    icon: DollarSign, 
    label: "Sales", 
    description: "Revenue & Growth",
    prompt: "We need a Sales Manager with 5-7 years experience and proven track record in B2B sales, team leadership, and CRM tools (Salesforce, HubSpot). The role is office-based with travel requirements (30% travel). Responsibilities include managing sales team, developing sales strategies, client relationship management, and achieving revenue targets. We're an established enterprise software company with a performance-driven culture. Budget is ₹10-16 lakhs." 
  },
  { 
    icon: MapPin, 
    label: "QA Engineer", 
    description: "Quality Assurance & Testing",
    prompt: "We need a QA Engineer with 3-5 years experience in manual and automated testing for web and mobile applications. Strong knowledge of test planning, test case design, regression testing, and bug tracking tools (Jira, TestRail) is required. Experience with automation frameworks like Selenium, Cypress, or Playwright is preferred. The role is hybrid with flexible work options. Responsibilities include designing and executing test plans, identifying and documenting defects, collaborating with developers and product managers, and ensuring high product quality before release. We're a fast-growing product company focused on reliability and user experience. Budget is ₹8-14 lakhs." 
  },
  { 
    icon: Palette, 
    label: "Design", 
    description: "UI/UX & Visual",
    prompt: "We need a UI/UX Designer with 3-5 years experience in user interface design, user experience research, Figma, Adobe Creative Suite, and prototyping tools. The role is remote-first with occasional team meetups. Responsibilities include designing user interfaces, conducting user research, creating wireframes and prototypes, collaborating with developers, and maintaining design systems. We're a design-focused product company with a collaborative and innovative culture. Budget is ₹7-12 lakhs." 
  },
  { 
    icon: UserCheck, 
    label: "HR", 
    description: "People Ops",
    prompt: "We need an HR Manager with 5-7 years experience in talent acquisition, employee relations, performance management, and HRIS systems. The role is office-based with some remote flexibility. Responsibilities include recruitment and onboarding, employee engagement initiatives, policy development, performance reviews, and handling employee relations. We're a mid-size organization with a people-first culture and focus on employee growth. Budget is ₹9-15 lakhs." 
  },
  { 
    icon: Calculator, 
    label: "Finance", 
    description: "FP&A & Reporting",
    prompt: "We need a Financial Analyst with 3-5 years experience in financial modeling, budgeting, forecasting, and financial reporting. Proficiency in Excel, accounting software (Tally, SAP), and data analysis tools required. The role is hybrid with 3 days in office. Responsibilities include financial planning and analysis, budget preparation, variance analysis, financial reporting, and supporting strategic decision-making. We're a well-established company with a structured and growth-oriented culture. Budget is ₹8-13 lakhs." 
  },
  { 
    icon: Headphones, 
    label: "Product Manager", 
    description: "Product Strategy & Delivery",
    prompt: "We need a Product Manager with 4-6 years experience in agile methodologies, stakeholder management, and data-driven decision making. The role is hybrid with 2-3 days in office. Responsibilities include product roadmap planning, cross-functional collaboration, user research, and driving product metrics. We're a mid-size SaaS company with a results-oriented culture. Budget is ₹15-22 lakhs." 
  },
  { 
    icon: Settings, 
    label: "Operations", 
    description: "Process & Delivery",
    prompt: "We need an Operations Manager with 5-8 years experience in process improvement, cross-functional coordination, vendor management, and KPI tracking. The role is hybrid with 2-3 days in office. Responsibilities include optimizing operational workflows, managing day-to-day execution, coordinating with multiple teams, ensuring SLAs, and driving continuous improvement initiatives. We're a fast-growing company with a high-ownership culture and focus on efficiency. Budget is ₹10-18 lakhs." 
  },
];

const examplePrompt = "We need a Senior Frontend Developer with 4-6 years experience in React and TypeScript. The role is remote-first with occasional office visits. Responsibilities include building responsive web applications, mentoring junior developers, and collaborating with UX designers. We're a fast-growing fintech startup with a collaborative culture. Budget is ₹10-15 lakhs.";

export const PostJobLanding = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('text'); // 'text', 'voice', 'create', 'upload'
  
  // Text AI state
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [copiedExample, setCopiedExample] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(true);
  const textareaRef = useRef(null);
  
  // Voice AI state
  const [answers, setAnswers] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const transcriptText = useRef("");
  const audioRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const lastTranscriptTimeRef = useRef(Date.now());
  const { transcript, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();
  const [showInstructions, setShowInstructions] = useState(false);
  const [micStatus, setMicStatus] = useState("idle");
  const [micMessage, setMicMessage] = useState("");
  const [speakerStatus, setSpeakerStatus] = useState("idle");
  const recorderRef = useRef(null);
  const audioContextRef = useRef(null);
  // The Realtime session owns this badge. Azure is transcript-only and must
  // never change the speaking/listening/thinking state.
  const [voiceStatus, setVoiceStatus] = useState("idle");
  const isSpeaking = voiceStatus === "speaking";
  const isRecording = voiceStatus === "listening";
  const voiceStatusDetails = {
    speaking: {
      title: "Sana is speaking",
      label: "Speaking...",
      helper: "Listen to Sana's next question.",
      icon: Volume2,
      tone: "border-blue-200 bg-blue-50 text-blue-700",
      avatar: "bg-blue-500",
    },
    listening: {
      title: "Sana is listening",
      label: "Listening...",
      helper: "Speak your answer naturally.",
      icon: Radio,
      tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
      avatar: "bg-emerald-500",
    },
    thinking: {
      title: "Sana is thinking",
      label: "Thinking...",
      helper: "Preparing the next question.",
      icon: Sparkles,
      tone: "border-amber-200 bg-amber-50 text-amber-700",
      avatar: "bg-amber-500",
    },
    connecting: {
      title: "Sana is connecting",
      label: "Connecting...",
      helper: "Starting your voice session.",
      icon: Loader2,
      tone: "border-slate-200 bg-slate-50 text-slate-700",
      avatar: "bg-slate-500",
    },
    idle: {
      title: "Sana is ready",
      label: "Waiting...",
      helper: "Ready for the conversation to begin.",
      icon: Sparkles,
      tone: "border-slate-200 bg-slate-50 text-slate-700",
      avatar: "bg-slate-500",
    },
  }[voiceStatus] || {
    title: "Sana is ready",
    label: "Waiting...",
    helper: "Ready for the conversation to begin.",
    icon: Sparkles,
    tone: "border-slate-200 bg-slate-50 text-slate-700",
    avatar: "bg-slate-500",
  };
  const compactVoiceStatus = {
    speaking: "Speaking",
    listening: "Listening",
    thinking: "Thinking",
    connecting: "Connecting",
    idle: "Ready",
  }[voiceStatus] || "Ready";
  const compactVoiceAnimation = voiceStatus === "thinking"
    ? "animate-spin"
    : (isSpeaking || isRecording) ? "animate-bounce" : "";
  const [contextualTipsOpen, setContextualTipsOpen] = useState(false);
  
  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const [isAIVoiceOn, setIsAIVoiceOn] = useState(false);

// Constants - add these near the top of your component
const JD_COMPLETION_MARKER = "[JD_CREATION_COMPLETE]";
const SESSION_LOCK_KEY = "jd_session_lock";

// Global lock to prevent duplicate sessions
let globalJDSessionLock = false;

// Add these refs to your component (add to existing refs section)
const pcRef = useRef(null);
const dcRef = useRef(null);
const audioElRef = useRef(null);
const ephemeralKeyRef = useRef(null);
const recognizerRef = useRef(null);
const realtimeMicStreamRef = useRef(null);
const recognitionActiveRef = useRef(false);
const conversationRef = useRef([]);
const isAIRespondingRef = useRef(false);
const isWaitingForUserRef = useRef(false);
const isJDCompletedRef = useRef(false);
const assistantResponseTextRef = useRef("");
const remoteAudioContextRef = useRef(null);
const remoteAudioAnalyserRef = useRef(null);
const remoteAudioSourceRef = useRef(null);
const remoteAudioMonitorFrameRef = useRef(null);
const remoteAudioSpeakingRef = useRef(false);
const remoteAudioHasPlayedRef = useRef(false);
const remoteAudioSilenceStartedAtRef = useRef(null);
const responseDoneRef = useRef(false);
const bargeInCancelledRef = useRef(false);
const cancelledResponseIdRef = useRef(null);
const processedResponseIdsRef = useRef(new Set());
const currentResponseIdRef = useRef(null);
const lastUserTextRef = useRef("");
const lastUserTsRef = useRef(0);

// Add these state variables (add to existing useState section)
const [connected, setConnected] = useState(false);
const [currentAIResponse, setCurrentAIResponse] = useState("");
const [liveTranscript, setLiveTranscript] = useState("");
const [isJDCompleted, setIsJDCompleted] = useState(false);
const [isGeneratingJD, setIsGeneratingJD] = useState(false);
const [conversation, setConversation] = useState([]);
const [convTick, setConvTick] = useState(0);
const [answer, setAnswer] = useState("");

useEffect(() => {
  isJDCompletedRef.current = isJDCompleted;
}, [isJDCompleted]);

const chatEndRef = useRef(null);

const companyId = localStorage.getItem("companyAccountId")
const companyName = localStorage.getItem("company_name")
const companyEmail = localStorage.getItem("employer_email")

const [generateCost, setGenerateCost] = useState('-');
const [balance, setBalance] = useState('-');
const [jobQuota, setJobQuota] = useState(null);   // {limit, used, remaining} active job posts
const [planName, setPlanName] = useState('');
// At the plan's active job-post cap → block all "create a job" actions.
const atJobLimit = !!(jobQuota && jobQuota.limit != null && jobQuota.remaining === 0);

useEffect(() => {
  async function loadCost() {
    try {
      const cost = await creditServices.getCost("AI_JOB_CREATED", 1);
      setGenerateCost(cost);
    } catch (error) {
      console.warn("[PostJobLanding] Failed to load JD generation cost:", error);
      setGenerateCost("-");
    }
  }

  loadCost();
}, []);

useEffect(() => {
  async function loadBalance() {
    try {
      const data = await creditServices.getBalance();
      setBalance(String(data?.balance ?? "-"));
    } catch (error) {
      console.warn("[PostJobLanding] Failed to load credit balance:", error);
      setBalance("-");
    }
  }

  loadBalance();
}, []);

useEffect(() => {
  async function loadQuota() {
    try {
      const cid = localStorage.getItem('companyAccountId');
      if (!cid) return;
      const res = await fetch(getApiUrl(`/api/entitlements/me?company_id=${encodeURIComponent(cid)}`), { credentials: 'include' });
      const data = await res.json();
      setJobQuota(data?.quotas?.active_job_posts || null);
      setPlanName(data?.plan_name || '');
    } catch (e) { /* fail-soft */ }
  }
  loadQuota();
}, []);

useEffect(() => {
  chatEndRef.current?.scrollIntoView({
    behavior: 'smooth',
    block: 'end'
  });
}, [conversation, liveTranscript, currentAIResponse]);

function setRealtimeMicrophoneEnabled(enabled) {
  realtimeMicStreamRef.current?.getAudioTracks().forEach((track) => {
    if (track.readyState === "live") {
      track.enabled = enabled;
    }
  });
}

function beginUserListening() {
  if (isJDCompletedRef.current || isWaitingForUserRef.current) return;

  isWaitingForUserRef.current = true;
  setRealtimeMicrophoneEnabled(true);
  setVoiceStatus("listening");
  startAzureRecognition();
}

function stopRemoteAudioMonitor() {
  if (remoteAudioMonitorFrameRef.current) {
    cancelAnimationFrame(remoteAudioMonitorFrameRef.current);
    remoteAudioMonitorFrameRef.current = null;
  }
  try {
    remoteAudioSourceRef.current?.disconnect();
    remoteAudioAnalyserRef.current?.disconnect();
  } catch (_) {}
  try {
    remoteAudioContextRef.current?.close();
  } catch (_) {}
  remoteAudioSourceRef.current = null;
  remoteAudioAnalyserRef.current = null;
  remoteAudioContextRef.current = null;
  remoteAudioSpeakingRef.current = false;
  remoteAudioHasPlayedRef.current = false;
  remoteAudioSilenceStartedAtRef.current = null;
}

function monitorRemoteAudio() {
  const analyser = remoteAudioAnalyserRef.current;
  if (!analyser) return;

  const samples = new Uint8Array(analyser.fftSize);
  const tick = () => {
    const activeAnalyser = remoteAudioAnalyserRef.current;
    if (!activeAnalyser) return;

    activeAnalyser.getByteTimeDomainData(samples);
    const rms = Math.sqrt(
      samples.reduce((sum, sample) => {
        const normalized = (sample - 128) / 128;
        return sum + normalized * normalized;
      }, 0) / samples.length
    );
    const now = performance.now();

    // After barge-in, ignore any final buffered Sana packets until the next
    // response is created. This keeps Listening visible immediately.
    if (bargeInCancelledRef.current) {
      remoteAudioSpeakingRef.current = false;
      remoteAudioSilenceStartedAtRef.current = null;
    } else if (rms >= REMOTE_AUDIO_RMS_THRESHOLD) {
      remoteAudioSpeakingRef.current = true;
      remoteAudioHasPlayedRef.current = true;
      remoteAudioSilenceStartedAtRef.current = null;
      setVoiceStatus("speaking");
    } else if (
      remoteAudioSpeakingRef.current ||
      (responseDoneRef.current && remoteAudioHasPlayedRef.current)
    ) {
      if (remoteAudioSilenceStartedAtRef.current === null) {
        remoteAudioSilenceStartedAtRef.current = now;
      }
      if (now - remoteAudioSilenceStartedAtRef.current >= REMOTE_AUDIO_SILENCE_DEBOUNCE_MS) {
        remoteAudioSpeakingRef.current = false;
        if (responseDoneRef.current) beginUserListening();
      }
    }

    remoteAudioMonitorFrameRef.current = requestAnimationFrame(tick);
  };

  remoteAudioMonitorFrameRef.current = requestAnimationFrame(tick);
}

function startRemoteAudioMonitor(stream) {
  stopRemoteAudioMonitor();
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    const context = new AudioContextClass();
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.2;
    source.connect(analyser);

    remoteAudioContextRef.current = context;
    remoteAudioSourceRef.current = source;
    remoteAudioAnalyserRef.current = analyser;
    context.resume().catch(() => {});
    monitorRemoteAudio();
  } catch (error) {
    console.warn("[JD] Unable to monitor remote audio", error);
  }
}

function cancelActiveResponseForBargeIn() {
  if (bargeInCancelledRef.current || !dcRef.current || dcRef.current.readyState !== "open") return;

  bargeInCancelledRef.current = true;
  cancelledResponseIdRef.current = currentResponseIdRef.current;
  try {
    dcRef.current.send(JSON.stringify({ type: "response.cancel" }));
  } catch (error) {
    console.warn("[JD] Unable to cancel active response", error);
  }

  if (audioElRef.current) {
    audioElRef.current.muted = true;
  }
}

async function startJDCreationSession() {
  setVoiceStatus("connecting");

  // Triple-layer protection against duplicate sessions
  const existingLock = sessionStorage.getItem(SESSION_LOCK_KEY);
  const now = Date.now();
  
  if (existingLock && (now - parseInt(existingLock, 10)) < 30000) {
    console.warn('[JD] Session already starting (sessionStorage lock)');
    return;
  }
  
  if (globalJDSessionLock) {
    console.warn('[JD] Session already starting (global lock)');
    return;
  }
  
  globalJDSessionLock = true;
  sessionStorage.setItem(SESSION_LOCK_KEY, now.toString());
  
  if (pcRef.current || audioElRef.current) {
    console.warn('[JD] Session resources already exist');
    globalJDSessionLock = false;
    sessionStorage.removeItem(SESSION_LOCK_KEY);
    return;
  }
  
  // Debug log removed — was state-machine noise on every session start.

  try {
    const resp = await fetch(getApiUrl("/api/jd-creation-session"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ 
        company_name: companyName
      })
    });
    
    const json = await resp.json();
    const EPHEMERAL_KEY =
      json?.session?.client_secret?.value ??
      json?.session?.client_secret ??
      json?.session?.token ??
      json?.client_secret?.value ??
      json?.client_secret ??
      null;
    
    if (!EPHEMERAL_KEY) {
      console.error("[JD] No ephemeral key returned", json);
      setVoiceStatus("idle");
      globalJDSessionLock = false;
      sessionStorage.removeItem(SESSION_LOCK_KEY);
      return;
    }
    
    ephemeralKeyRef.current = EPHEMERAL_KEY;

    const pc = new RTCPeerConnection();
    pcRef.current = pc;
    
    const audioEl = document.createElement("audio");
    audioEl.autoplay = true;
    audioEl.muted = false;
    audioElRef.current = audioEl;
    
    // Audio event listeners
    audioEl.addEventListener('canplay', () => {
      audioEl.play().catch((err) => {
        console.warn('[audio] canplay blocked:', err?.message);
      });
    });
    audioEl.addEventListener('playing', () => {
      remoteAudioContextRef.current?.resume().catch(() => {});
    });
    
    pc.ontrack = (ev) => {
      if (ev.streams && ev.streams[0]) {
        audioEl.srcObject = ev.streams[0];
        startRemoteAudioMonitor(ev.streams[0]);
      }
    };
    
    const dc = pc.createDataChannel("oai-events");
    dcRef.current = dc;

    // Realtime receives the user's actual microphone track so VAD can create
    // durable turns. Browser echo cancellation prevents Sana's audio from
    // being treated as the user's speech and keeps barge-in available.
    try {
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      realtimeMicStreamRef.current = micStream;
      micStream.getAudioTracks().forEach((track) => {
        track.enabled = true;
        pc.addTrack(track, micStream);
      });
    } catch (error) {
      console.error("[JD] Unable to access microphone for Realtime", error);
      setVoiceStatus("idle");
      throw error;
    }
    
    dc.onopen = () => {
      setConnected(true);
      // Debug log removed — was state-machine noise on every channel open.

      // Send initial greeting trigger
      const sysPayload = {
        type: "response.create",
        response: {
          instructions: json.instructions || "SYSTEM: Start JD creation. Greet the user warmly.",
          // GA: `modalities` → `output_modalities`; ["audio"] yields voice + transcript.
          output_modalities: ["audio"]
        }
      };
      
      try {
        dc.send(JSON.stringify(sysPayload));
      } catch (e) {
        console.warn("dc.send sysPayload failed", e);
      }
    };
    
    dc.onmessage = (ev) => {
      handleRealtimeMessage(ev);
    };
    
    // WebRTC offer/answer.
    // GA Realtime: SDP is exchanged at /v1/realtime/calls (the old beta
    // /v1/realtime?model=… is retired → beta_api_shape_disabled). The model is
    // encoded in the ephemeral token, so no model query param is sent here.
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const sdpResp = await fetch(`https://api.openai.com/v1/realtime/calls`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${EPHEMERAL_KEY}`,
        "Content-Type": "application/sdp"
      },
      body: offer.sdp,
    });
    
    if (!sdpResp.ok) {
      const txt = await sdpResp.text();
      console.error("[JD] SDP exchange failed:", sdpResp.status, txt);
      setVoiceStatus("idle");
      globalJDSessionLock = false;
      sessionStorage.removeItem(SESSION_LOCK_KEY);
      return;
    }
    
    const answer = await sdpResp.text();
    await pc.setRemoteDescription({ type: "answer", sdp: answer });

    // Debug log removed — was state-machine noise on every session start.

  } catch (err) {
    console.error("[JD] startJDCreationSession error", err);
    setVoiceStatus("idle");
    globalJDSessionLock = false;
    sessionStorage.removeItem(SESSION_LOCK_KEY);
  }
}

async function stopRealtimeSession() {
  isWaitingForUserRef.current = false;
  responseDoneRef.current = false;
  bargeInCancelledRef.current = false;
  cancelledResponseIdRef.current = null;
  currentResponseIdRef.current = null;
  stopRemoteAudioMonitor();
  setVoiceStatus("idle");

  try {
    // Clean up audio element
    if (audioElRef.current) {
      try {
        audioElRef.current.pause();
        audioElRef.current.srcObject = null;
        audioElRef.current.remove();
      } catch (e) {}
      audioElRef.current = null;
    }
    
    if (pcRef.current) {
      try {
        pcRef.current.getSenders().forEach(s => s.track?.stop());
      } catch (e) {}
      pcRef.current.close();
    }
    realtimeMicStreamRef.current?.getTracks().forEach((track) => track.stop());
    realtimeMicStreamRef.current = null;
  } catch (e) {}
  
  pcRef.current = null;
  dcRef.current = null;
  ephemeralKeyRef.current = null;
  globalJDSessionLock = false;
  sessionStorage.removeItem(SESSION_LOCK_KEY);
  // Debug log removed — was state-machine noise on every session stop.
  setConnected(false);
}

async function handleRealtimeMessage(ev) {
  try {
    const dataRaw = ev.data?.toString?.() ?? "";
    let data;
    try {
      data = JSON.parse(dataRaw);
    } catch {
      data = null;
    }
    
    const type = (data?.type || "").toString() || "";
    // Debug log removed — was high-volume event-stream noise.

    // Helper to validate text
    function isValidChatText(t) {
      if (!t) return false;
      const txt = String(t).trim();
      if (!txt) return false;
      if (txt.startsWith("{") || txt.startsWith("[") || txt.startsWith("<")) return false;
      const low = txt.toLowerCase();
      if (low.includes("error") || low.includes("exception")) return false;
      if (txt.length <= 2 || /^[\W_]+$/.test(txt)) return false;
      return true;
    }
    
    const extracted = data ? extractTextFromRealtimeEvent(data) : dataRaw;

    const responseId = data?.response_id || data?.response?.id || null;

    // Realtime VAD now receives the real microphone track. These events are
    // the source of truth for the user's turn, rather than a synthetic text
    // instruction from Azure.
    if (type === "input_audio_buffer.speech_started") {
      // The native Realtime microphone stays enabled while Sana speaks. If the
      // user starts talking over her, stop the active answer immediately.
      if (remoteAudioSpeakingRef.current || isAIRespondingRef.current) {
        cancelActiveResponseForBargeIn();
      }
      isWaitingForUserRef.current = true;
      setVoiceStatus("listening");
      return;
    }

    if (type === "input_audio_buffer.speech_stopped") {
      if (isWaitingForUserRef.current) {
        setVoiceStatus("thinking");
      }
      return;
    }
    
    // Only a new response is allowed to start a new status turn. Output-item
    // events can belong to the just-cancelled response, so they never reset it.
    if (type === "response.created") {
      currentResponseIdRef.current = responseId;
      setCurrentAIResponse("");
      isWaitingForUserRef.current = false;
      responseDoneRef.current = false;
      bargeInCancelledRef.current = false;
      cancelledResponseIdRef.current = null;
      remoteAudioHasPlayedRef.current = false;
      remoteAudioSilenceStartedAtRef.current = null;
      setRealtimeMicrophoneEnabled(true);
      if (audioElRef.current) audioElRef.current.muted = false;
      stopAzureRecognition();
      assistantResponseTextRef.current = "";
      setVoiceStatus("thinking");
      isAIRespondingRef.current = true;
      return;
    }

    if (type === "response.output_item.added") return;

    // A cancelled response may still deliver buffered audio/transcript events.
    // Ignore all of them until OpenAI creates the user's next response.
    const isCancelledResponse =
      !responseId ||
      !cancelledResponseIdRef.current ||
      responseId === cancelledResponseIdRef.current;
    if (bargeInCancelledRef.current && isCancelledResponse && type.startsWith("response.")) {
      if (type === "response.done") {
        setCurrentAIResponse("");
        isAIRespondingRef.current = false;
      }
      return;
    }
    
    // Audio chunks are sent ahead of browser playback. The remote-audio
    // analyser, rather than this event, changes the visible status to Speaking.
    if (type === "response.output_audio.delta" || type === "response.audio.delta") {
      return;
    }

    // Streaming text/transcript delta
    if (type === "response.audio_transcript.delta" || type === "response.output_text.delta" || /delta/i.test(type)) {
      const deltaText = data?.delta || extracted || "";
      if (isValidChatText(deltaText)) {
        assistantResponseTextRef.current += deltaText;
        setCurrentAIResponse(prev => prev + deltaText);
        isAIRespondingRef.current = true;
      }
      return;
    }
    
    // Response complete. GA renamed these events: beta emitted
    // `response.audio_transcript.done`, GA emits
    // `response.output_audio_transcript.done` (and `response.output_text.done`).
    // Match all variants — else the final AI line never commits and the
    // streaming text vanishes when `response.done` clears it.
    if (
      type === "response.audio_transcript.done" ||
      type === "response.output_audio_transcript.done" ||
      type === "response.output_text.done" ||
      /(audio_transcript|output_text)\.done$/.test(type)
    ) {
      const finalText = (data?.transcript || data?.text || extracted || "").trim();
      
      if (!finalText || !isValidChatText(finalText)) {
        setCurrentAIResponse("");
        isAIRespondingRef.current = false;
        return;
      }
      
      // Audio transcription can omit the bracketed marker even when Sana says
      // the required finalisation sentence. Accept that sentence as the same
      // frontend completion signal; the backend generation API is unchanged.
      const hasCompletionSignal =
        finalText.includes(JD_COMPLETION_MARKER) ||
        /i have everything i need to create (?:your )?job description/i.test(finalText);

      if (hasCompletionSignal) {
        // Debug log removed — was state-machine noise.
        isJDCompletedRef.current = true;
        setIsJDCompleted(true);
        
        // Clean up the text (remove marker)
        const cleanText = finalText.replace(JD_COMPLETION_MARKER, "").trim();
        
        const newMessage = {
          role: 'assistant',
          text: cleanText,
          id: responseId || `ai-${Date.now()}`,
          timestamp: Date.now()
        };
        
        conversationRef.current = [...conversationRef.current, newMessage];
        setConversation([...conversationRef.current]);
        setCurrentAIResponse("");

        // ✅ Convert conversation to text
        const compiledPrompt = conversationRef.current
          .filter(m => m?.text)
          .map(m => `${m.role === 'assistant' ? 'AI' : 'User'}: ${m.text}`)
          .join('\n');
        
        try {
          const response = await axios.post(
            getApiUrl("/api/generate_jd_ai"),
            {
              prompt: compiledPrompt,
              company_id: companyId,
              company_email: companyEmail,
              company_name: companyName,
            }
          );
          const jd = response.data?.job_details;
          if (!jd) throw new Error("JD generation returned no job details");

          localStorage.setItem('generatedJdData', JSON.stringify(jd));
          isAIRespondingRef.current = false;

          setTimeout(() => {
            handleJDGeneration();
          }, 1000);
        } catch (error) {
          console.error("[JD] Final JD generation failed", error);
          const msg = error?.response?.data?.error
            || (error?.response?.status === 403
                ? "You're at your plan's active job-post limit. Close a job or upgrade before creating another."
                : "Job description generation failed. Please try again.");
          toast.error(msg);
          isJDCompletedRef.current = false;
          setIsJDCompleted(false);
          setVoiceStatus("idle");
        }

        return;
      }
      
      // Dedupe check
      if (responseId && processedResponseIdsRef.current.has(responseId)) {
        setCurrentAIResponse("");
        isAIRespondingRef.current = false;
        return;
      }
      if (responseId) processedResponseIdsRef.current.add(responseId);
      assistantResponseTextRef.current = finalText;
      
      const newMessage = {
        role: 'assistant',
        text: finalText,
        id: responseId || `ai-${Date.now()}`,
        timestamp: Date.now()
      };
      
      conversationRef.current = [...conversationRef.current, newMessage];
      setConversation([...conversationRef.current]);
      setCurrentAIResponse("");
      isAIRespondingRef.current = false;
      
      return;
    }
    
    // `response.done` means the server has sent all audio. The remote-audio
    // monitor waits for actual browser playback silence before listening.
    if (type === "response.done") {
      setCurrentAIResponse("");
      isAIRespondingRef.current = false;
      scheduleAzureListening();
      return;
    }
    
    // Errors
    if (type.includes("error") || data?.error) {
      console.error("[JD] Error:", data);
      setCurrentAIResponse("");
      isAIRespondingRef.current = false;
      setVoiceStatus("idle");
      return;
    }
    
  } catch (e) {
    console.error('[JD] Error parsing message:', e);
  }
}

function extractTextFromRealtimeEvent(data) {
  if (!data) return "";
  return data.transcript || data.text || data.delta || data.content || "";
}

function scheduleAzureListening() {
  if (
    isJDCompletedRef.current ||
    isWaitingForUserRef.current ||
    recognitionActiveRef.current
  ) {
    return;
  }

  // Server completion happens before the browser necessarily finishes playing
  // the last remote audio packet. The analyser turns Listening on only after
  // that real audio becomes quiet; no word count or estimated playback time.
  isAIRespondingRef.current = false;
  responseDoneRef.current = true;

  if (remoteAudioSpeakingRef.current) {
    setVoiceStatus("speaking");
  } else if (remoteAudioHasPlayedRef.current) {
    remoteAudioSilenceStartedAtRef.current = performance.now();
  } else if (!remoteAudioAnalyserRef.current) {
    // Audio analysis is unavailable in this browser, so do not leave the user
    // in Speaking forever. Supporting browsers use the real audio signal above.
    beginUserListening();
  }
}

// ============================================================================
// AZURE STT FUNCTIONS
// ============================================================================

const startAzureRecognition = async () => {
  if (!recognizerRef.current) {
    console.warn("[Azure] Recognizer is not ready; live transcript is unavailable");
    return;
  }
  if (recognitionActiveRef.current) return;
  
  recognitionActiveRef.current = true;
  try {
    recognizerRef.current.startContinuousRecognitionAsync(
      () => {
        setLiveTranscript("");
        // Debug log removed — was state-machine noise on every recognition start.
      },
      (err) => {
        console.error('[Azure] Start error:', err);
        recognitionActiveRef.current = false;
      }
    );
  } catch (err) {
    console.error("startAzureRecognition error", err);
    recognitionActiveRef.current = false;
  }
};

const stopAzureRecognition = async () => {
  if (!recognizerRef.current || !recognitionActiveRef.current) return;
  recognitionActiveRef.current = false;
  
  return new Promise((resolve) => {
    recognizerRef.current.stopContinuousRecognitionAsync(
      () => {
        setLiveTranscript("");
        // Debug log removed — was state-machine noise on every recognition stop.
        resolve();
      },
      (err) => {
        console.warn("Azure stop error", err);
        resolve();
      }
    );
  });
};

const cleanupRecognizer = async () => {
  if (recognizerRef.current) {
    recognitionActiveRef.current = false;
    try {
      await stopAzureRecognition();
    } catch (_) {}
    try {
      recognizerRef.current.close();
    } catch (_) {}
    recognizerRef.current = null;
  }
};

// Initialize Azure STT - add this useEffect
useEffect(() => {
  let mounted = true;
  
  const initializeAzureSTT = async () => {
    try {
      if (!speechConfig) {
        console.error("Missing Speech config");
        return;
      }
      
      if (recognizerRef.current) await cleanupRecognizer();
      
      let recognizer;
      try {
        const ac = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
        recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, ac);
      } catch (err) {
        const pushStream = SpeechSDK.AudioInputStream.createPushStream();
        const ac = SpeechSDK.AudioConfig.fromStreamInput(pushStream);
        recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, ac);
      }
      
      // Interim transcription (live)
      recognizer.recognizing = (s, e) => {
        if (!mounted || isJDCompleted) return;
        const interimText = e.result?.text ?? "";
        // Debug log removed — was high-volume transcript-leak on every interim event.

        setLiveTranscript(interimText);
      };
      
      // Final recognized text
      recognizer.recognized = (s, e) => {
        if (!mounted || isJDCompleted) return;
        const r = e.result;

        if (r.reason === SpeechSDK.ResultReason.RecognizedSpeech && r.text) {
          const newText = r.text.trim();
          if (!newText) return;
          
          const now = Date.now();
          
          // Dedupe
          if (lastUserTextRef.current === newText && (now - lastUserTsRef.current) < 3000) {
            return;
          }
          
          lastUserTextRef.current = newText;
          lastUserTsRef.current = now;
          
          setLiveTranscript("");
          
          // Add to conversation
          const newMessage = {
            role: 'user',
            text: newText,
            id: `user-${now}`,
            timestamp: now
          };
          
          conversationRef.current = [...conversationRef.current, newMessage];
          setConversation([...conversationRef.current]);
          
          // Update your existing transcript ref for UI display
          transcriptText.current = newText;
          
          // OpenAI Realtime receives this same microphone audio directly via
          // WebRTC. Azure is display-only here; do not send a second,
          // temporary USER_ANSWER response that would break turn tracking.
          stopAzureRecognition();
        }
      };
      
      recognizer.canceled = (s, e) => {
        console.warn("Azure canceled:", e.reason);
        recognitionActiveRef.current = false;
      };
      
      recognizer.sessionStopped = (s, e) => {
        // Debug log removed — was state-machine noise on every session stop.
        recognitionActiveRef.current = false;
      };
      
      recognizerRef.current = recognizer;
      
    } catch (err) {
      console.error("Error initializing Azure STT:", err);
    }
  };
  
  initializeAzureSTT();
  
  return () => {
    mounted = false;
    cleanupRecognizer();
  };
}, []); // Empty dependency array - only run once on mount

// ============================================================================
// JD GENERATION FUNCTION
// ============================================================================

async function handleJDGeneration() {
  // Debug log removed — was state-machine noise.
  setIsGeneratingJD(true);
  
  try {
    // Stop recognition
    await stopAzureRecognition();
    
    // Stop realtime session
    await stopRealtimeSession();

    setIsAIVoiceOn(false);
    
    navigate("/hirewithus/postjob/create", { state: { skipToReview: true } });
    
  } catch (error) {
    console.error('[JD] Error generating JD:', error);
    alert('Failed to generate job description. Please try again.');
    setIsGeneratingJD(false);
    setIsJDCompleted(false);
  }
}

async function handleCancelVoiceSession() {
  try {
    await stopAzureRecognition();
    await stopRealtimeSession();
  } catch (error) {
    console.warn("[JD] Voice session cleanup during cancel failed", error);
  } finally {
    setCurrentAIResponse("");
    setLiveTranscript("");
    setIsAIVoiceOn(false);
  }
}

useEffect(() => {
  return () => {
    stopRealtimeSession();
    cleanupRecognizer();
  };
}, []);

  // Auto-focus textarea when text tab is active
  useEffect(() => {
    if (activeTab === 'text' && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [activeTab]);

  // Voice AI effects
  useEffect(() => {
    if (transcript && transcript.trim().length > 0) {
      transcriptText.current = transcript;
      lastTranscriptTimeRef.current = Date.now();
    }
  }, [transcript]);

  useEffect(() => {
    if (currentIndex !== 0 && !showInstructions) {
      handleFlow();
    }
  }, [currentIndex]);

  // Text AI handlers
  const handleUseExample = () => {
    setPrompt(examplePrompt);
    setCopiedExample(true);
    setTimeout(() => setCopiedExample(false), 2000);
  };

  const handleQuickPrompt = (quickPrompt) => {
    setPrompt(quickPrompt);
    setError('');
  };

  const handleTextSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) {
      setError('Please enter a prompt to generate job description');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const urls = getApiUrlsWithFallback('/api/generate_jd_ai');
      let response;
      let lastError;

      for (const url of urls) {
        try {
          response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prompt, company_id: companyId, company_email: companyEmail, company_name: companyName })
          });
          if (response.ok) break;
        } catch (err) {
          lastError = err;
          continue;
        }
      }

      if (!response || !response.ok) {
        // Surface a real API error (e.g. plan job-post limit / insufficient credits).
        let apiErr = null;
        try { apiErr = response ? await response.clone().json() : null; } catch { /* non-JSON */ }
        if (response && (response.status === 403 || response.status === 402) && apiErr?.error) {
          setError(apiErr.error);
          setIsGenerating(false);
          return;
        }
        throw lastError || new Error(apiErr?.error || 'Failed to connect to API servers');
      }

      const data = await response.json();
      const generatedJdData = data.job_details;
      localStorage.setItem('generatedJdData', JSON.stringify(generatedJdData));
      setIsGenerating(false);
      navigate('/hirewithus/postjob/create', { state: { skipToReview: true } });
    } catch (error) {
      console.error('JD Generation Error:', error);
      setError('Failed to generate job description. Please try again.');
      setIsGenerating(false);
    }
  };

  // Voice AI handlers
  const getContextualTips = () => {
    const tipsByQuestion = {
      0: [
        { icon: MessageSquare, text: "Be specific: 'Senior Frontend Developer' is better than 'Developer'" },
        { icon: CheckCircle2, text: "Include level if relevant: Junior, Mid-level, Senior, Lead, etc." }
      ],
      1: [
        { icon: CheckCircle2, text: "Specify years: '3-5 years' or '5+ years experience'" },
        { icon: MessageSquare, text: "Mention industry experience if relevant" }
      ],
      2: [
        { icon: Sparkles, text: "Include currency and range: '₹10-15 lakhs' or 'Competitive salary'" },
        { icon: CheckCircle2, text: "Mention if negotiable or includes benefits" }
      ],
      3: [
        { icon: MessageSquare, text: "Keep it concise - 2-3 sentences summarizing the role" },
        { icon: CheckCircle2, text: "Focus on what makes this role unique" }
      ],
      4: [
        { icon: CheckCircle2, text: "List 3-5 main responsibilities" },
        { icon: MessageSquare, text: "Use action verbs: 'Develop', 'Manage', 'Collaborate'" }
      ],
      5: [
        { icon: Sparkles, text: "Be specific: 'React, TypeScript, Node.js' not just 'JavaScript'" },
        { icon: CheckCircle2, text: "Separate must-have from nice-to-have skills" }
      ],
      6: [
        { icon: MessageSquare, text: "Specify: 'Remote', 'Hybrid', 'On-site', or 'Flexible'" },
        { icon: CheckCircle2, text: "Include city/region if location-specific" }
      ]
    };
    return tipsByQuestion[currentIndex] || [
      { icon: CheckCircle2, text: "Speak clearly and naturally" },
      { icon: MessageSquare, text: "Take your time to provide complete answers" }
    ];
  };

  const handleMicTest = async () => {
    setMicStatus("Testing");
    setMicMessage("");
    const recorder = await initializeRecorder();
    if (!recorder) {
      setMicStatus("Error");
      setMicMessage("Failed to initialize microphone.");
      return;
    }
    try {
      recorder.start();
      setTimeout(async () => {
        const { blob } = await recorder.stop();
        const audioURL = URL.createObjectURL(blob);
        const formData = new FormData();
        formData.append("audio", blob);
        setMicStatus("Tested");
        setMicMessage("Microphone test completed successfully.");
      }, 5000);
    } catch (error) {
      setMicStatus("Error");
      setMicMessage("Microphone access denied.");
    }
  };

  const handleSpeakerTest = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      gainNode.gain.setValueAtTime(1, audioContext.currentTime);
      oscillator.start();
      setSpeakerStatus("Testing");
      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
        setSpeakerStatus("Tested");
      }, 1000);
    } catch (error) {
      setSpeakerStatus("Error");
    }
  };

  const initializeRecorder = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const recorder = new Recorder(audioContext);
      recorder.init(stream);
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      source.connect(analyser);
      audioContextRef.current = audioContext;
      recorderRef.current = recorder;
      return recorder;
    } catch (error) {
      return null;
    }
  };

  const handleFlow = async () => {
    if (currentIndex >= questions.length - 1) {
      const prompt = questions.map((q, i) => `${q} ${answers[i]}`).join("\n");
      const res = await axios.post(getApiUrl("/api/generate_jd_ai"), { prompt });
      const jd = res.data.job_details;
      const thankYouMessage = await axios.post(getApiUrl("/api/generate_audio"), {
        text: "Thank you. I have created the job description based on your responses. Please review and save it.",
      }, { responseType: "blob" });
      setVoiceStatus("speaking");
      const url = URL.createObjectURL(thankYouMessage.data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.play();
      audio.onended = () => {
        setVoiceStatus("idle");
        localStorage.setItem('generatedJdData', JSON.stringify(jd));
        setIsAIVoiceOn(false);
        navigate("/hirewithus/postjob/create", { state: { skipToReview: true } });
      };
      return;
    }
    const res = await axios.post(getApiUrl("/api/generate_audio"), {
      text: questions[currentIndex],
    }, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const audio = new Audio(url);
    setVoiceStatus("speaking");
    audioRef.current = audio;
    audio.play();
    audio.onended = () => {
      startListening();
      setVoiceStatus("listening");
    };
  };

  const startListening = () => {
    setVoiceStatus("listening");
    resetTranscript();
    transcriptText.current = "";
    SpeechRecognition.startListening({ continuous: true });
    lastTranscriptTimeRef.current = Date.now();
    if (silenceTimerRef.current) clearInterval(silenceTimerRef.current);
    silenceTimerRef.current = setInterval(() => {
      const now = Date.now();
      if (now - lastTranscriptTimeRef.current > 5000) {
        stopListening();
      }
    }, 1000);
  };

  const stopListening = () => {
    setVoiceStatus("idle");
    SpeechRecognition.stopListening();
    clearInterval(silenceTimerRef.current);
    const updated = [...answers];
    updated[currentIndex] = transcriptText.current.trim() || "No answer";
    setAnswers(updated);
    transcriptText.current = "";
    const acknowledgementPhrases = [
      "Thank you for sharing.",
      "Got it, thank you.",
      "That's clear.",
      "Understood.",
      "Thanks, I have got that.",
      "Perfect.",
      "Thanks for clarifying."
    ];
    const randomPhrase = acknowledgementPhrases[Math.floor(Math.random() * acknowledgementPhrases.length)];
    const playAcknowledgement = async () => {
      const ackRes = await axios.post(getApiUrl("/api/generate_audio"), {
        text: randomPhrase,
      }, { responseType: "blob" });
      const ackUrl = URL.createObjectURL(ackRes.data);
      const ackAudio = new Audio(ackUrl);
      audioRef.current = ackAudio;
      ackAudio.play();
      ackAudio.onended = () => {
        setCurrentIndex(prev => prev + 1);
      };
    };
    playAcknowledgement();
  };

  const handleStartVoiceAI = () => {
    if (!browserSupportsSpeechRecognition) {
      toast.error("Your browser doesn't support speech recognition.");
      return;
    }
    setShowInstructions(true);
  };

  const handleStartVoiceFlow = () => {
    if (micStatus === "Tested" && speakerStatus === "Tested") {
      setShowInstructions(false);
      // Reset state
      conversationRef.current = [];
      setConversation([]);
      processedResponseIdsRef.current.clear();
      setIsJDCompleted(false);
      setIsGeneratingJD(false);
      isJDCompletedRef.current = false;
      setVoiceStatus("connecting");
      setIsAIVoiceOn(true);
      // Start the Realtime AI session
      startJDCreationSession();
    }
  };

  // Upload handlers
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['.pdf', '.doc', '.docx', '.txt'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      
      if (!allowedTypes.includes(fileExtension)) {
        setUploadError('Please upload a valid file (PDF, DOC, DOCX, or TXT)');
        setSelectedFile(null);
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        setUploadError('File size should not exceed 10MB');
        setSelectedFile(null);
        return;
      }
      
      setUploadError('');
      setSelectedFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      const event = { target: { files: [file] } };
      handleFileChange(event);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setUploadError('Please select a file to upload');
      return;
    }
    
    if (isUploading) {
      return;
    }
    
    setIsUploading(true);
    setUploadError('');
    setUploadProgress(10);
    
    try {
      const urls = getApiUrlsWithFallback('/api/upload_jd');
      let response;
      let lastError;
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      // 🔹 Send to backend
      formData.append("company_id", companyId);
      formData.append("company_name", companyName);
      formData.append("company_email", companyEmail);
      setUploadProgress(30);
      
      for (const url of urls) {
        try {
          response = await fetch(url, {
            method: 'POST',
            body: formData
          });
          
          if (response.ok) {
            break;
          }
        } catch (err) {
          lastError = err;
          continue;
        }
      }

      if (!response) {
        throw lastError || new Error('Failed to connect to API servers');
      }

      setUploadProgress(60);

      if (!response.ok) {
        // Surface plan-limit / insufficient-credit messages (403 upgrade_required,
        // 402 insufficient_credits) like the text & voice paths do.
        let apiErr = null;
        try { apiErr = await response.json(); } catch (_) { /* not JSON */ }
        if ((response.status === 403 || response.status === 402) && apiErr?.error) {
          throw new Error(apiErr.error);
        }
        throw new Error(`Failed to generate job description: ${response.status}`);
      }

      const data = await response.json();
      setUploadProgress(80);
      
      const generatedJdData = data.job_details;
      localStorage.setItem('generatedJdData', JSON.stringify(generatedJdData));
      
      setUploadProgress(100);
      setShowSuccess(true);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      setTimeout(() => {
        navigate('/hirewithus/postjob/create', { state: { skipToReview: true } });
      }, 2000);
    } catch (error) {
      console.error('JD Generation Error:', error);
      setUploadError(error.message || 'Failed to upload job description. Please try again.');
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <EmployerLayout>
      <div className="flex flex-col w-full gap-8 relative">
        {/* Animated background gradient */}
        <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '0s' }} />
          <div className="absolute top-20 -right-4 w-72 h-72 bg-primary/3 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 text-left relative">

          {/* LEFT SIDE */}
          <div className="flex flex-col gap-4 max-w-3xl">
            <Badge 
              variant="outline" 
              className="w-fit bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 text-primary border-primary/30 flex items-center gap-2 backdrop-blur-sm shadow-sm"
            >
              <Zap className="h-4 w-4 animate-pulse" />
              <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent font-semibold">
                AI Hiring Assistant
              </span>
            </Badge>

            <div className="space-y-2">
              <Heading level="h4" className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                Create your job description in minutes
              </Heading>

              <Text className="text-sm text-muted-foreground leading-relaxed">
                Type a brief with our AI Text Agent, or speak naturally with our AI Voice Agent.
                Choose your preferred way to create a polished job description.
              </Text>
            </div>
          </div>

          {/* RIGHT SIDE */}
          <CreditWidget balance={balance} />

        </div>

        {/* Job-post quota (plan-wise) */}
        {jobQuota && jobQuota.limit != null && (
          <div className={`mb-5 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${jobQuota.remaining === 0 ? 'border-red-200 bg-red-50 text-red-700' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
            {jobQuota.remaining === 0 ? (
              <span>You've used all <b>{jobQuota.limit}</b> active job post{jobQuota.limit === 1 ? '' : 's'} on your {planName || 'current'} plan. Close a job or upgrade to post another.</span>
            ) : (
              <span><b>{jobQuota.remaining}</b> of <b>{jobQuota.limit}</b> job post{jobQuota.limit === 1 ? '' : 's'} left on your {planName || 'current'} plan.</span>
            )}
          </div>
        )}

        {/* Main AI Assistant Interface */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-2xl grid-cols-4 mb-6">
            <TabsTrigger value="text" className="gap-2">
              <Sparkles className="h-4 w-4" />
              AI Text Agent
            </TabsTrigger>
            <TabsTrigger value="voice" className="gap-2">
              <Mic className="h-4 w-4" />
              AI Voice Agent
            </TabsTrigger>
            <TabsTrigger value="create" className="gap-2">
              <FileEdit className="h-4 w-4" />
              Manual Create
            </TabsTrigger>
            <TabsTrigger value="upload" className="gap-2">
              <Upload className="h-4 w-4" />
              Upload JD
            </TabsTrigger>
          </TabsList>

          {/* AI Text Agent Tab */}
          <TabsContent value="text" className="space-y-6">
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-card to-card">
              <CardContent className="pt-6">
                <form onSubmit={handleTextSubmit} className="space-y-6">
                  {/* Quick Start Templates */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap className="h-3.5 w-3.5 text-primary" />
                        <Label className="text-xs font-semibold text-foreground uppercase tracking-wide">
                          Quick Start Templates
                        </Label>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {quickPrompts.map((item, idx) => {
                        const Icon = item.icon;
                        return (
                          <Button
                            key={idx}
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleQuickPrompt(item.prompt)}
                            disabled={isGenerating}
                            className={cn(
                              "gap-2 h-9 px-3 text-xs",
                              "hover:border-primary/50 hover:bg-primary/5 hover:text-primary",
                              "transition-all duration-200"
                            )}
                          >
                            <Icon className="h-3.5 w-3.5" />
                            <span className="font-medium">{item.label}</span>
                            <span className="text-muted-foreground/70">•</span>
                            <span className="text-muted-foreground text-[10px]">{item.description}</span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Main Textarea */}
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="prompt" className="text-base">
                        Describe your ideal candidate
                      </Label>
                      <Text variant="muted" size="xs" className="text-xs tabular-nums">
                        {prompt.length}
                      </Text>
                    </div>
                    <div className="relative">
                      <Textarea
                        ref={textareaRef}
                        id="prompt"
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder="E.g., We need a Senior Frontend Developer with 4-6 years experience in React and TypeScript. The role is remote-first with occasional office visits. Responsibilities include building responsive web applications, mentoring junior developers, and collaborating with UX designers. Budget is ₹10-15 lakhs."
                        rows={8}
                        className="min-h-[180px] resize-y text-sm leading-relaxed pr-48"
                        disabled={isGenerating}
                      />
                      <div className="absolute bottom-3 right-3 flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setActiveTab('voice')}
                          disabled={isGenerating}
                          className="gap-1.5 shadow-sm"
                        >
                          <Mic className="h-3.5 w-3.5" />
                          Use Voice
                        </Button>
                        <Button
                          type="submit"
                          variant="default"
                          size="sm"
                          disabled={isGenerating || !prompt.trim() || (balance < generateCost) || atJobLimit}
                          title={atJobLimit ? "You're at your plan's active job-post limit. Close a job or upgrade." : undefined}
                          className="gap-2 shadow-md"
                          style={{
                            backgroundColor: getColor('primary'),
                            color: getColor('primaryForeground'),
                            borderColor: getColor('primary')
                          }}
                        >
                          {isGenerating ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-3.5 w-3.5" />
                              Generate
                              <span className="ml-2 text-xs text-gray-500">
                                -{generateCost} credits
                              </span>
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-1.5 flex-1">
                        <Lightbulb className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <Text variant="muted" size="xs" className="text-xs leading-relaxed">
                          Be specific about requirements, skills, and company culture for better results
                        </Text>
                      </div>
                      {isGenerating && (
                        <Text variant="muted" size="xs" className="text-xs animate-pulse flex-shrink-0">
                          AI is analyzing your requirements...
                        </Text>
                      )}
                    </div>
                  </div>

                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription className="text-sm">{error}</AlertDescription>
                    </Alert>
                  )}
                </form>
              </CardContent>
            </Card>

            {/* Tips & Example Section */}
            <Collapsible open={tipsOpen} onOpenChange={setTipsOpen}>
              <Card className="border-l-2 border-l-primary">
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="p-1.5 rounded-md bg-primary/10">
                          <Lightbulb className="h-4 w-4 text-primary" />
                        </div>
                        <div className="text-left">
                          <CardTitle className="text-base font-semibold">Writing Tips & Best Practices</CardTitle>
                          <CardDescription className="text-xs">Learn how to write effective prompts</CardDescription>
                        </div>
                      </div>
                      <ChevronDown className={cn(
                        "h-4 w-4 text-muted-foreground transition-transform duration-200",
                        tipsOpen && "transform rotate-180"
                      )} />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                      {[
                        { icon: Briefcase, title: "Job Title & Level", desc: "Include specific role (e.g., 'Senior Frontend Developer', 'Junior Data Scientist')" },
                        { icon: Zap, title: "Required Skills", desc: "Mention specific technologies (e.g., 'React, Python, AWS, SQL')" },
                        { icon: MapPin, title: "Work Arrangement", desc: "Specify if remote, hybrid, or office-based" },
                        { icon: Clock, title: "Experience Level", desc: "Include years of experience needed (e.g., '3-5 years experience')" },
                        { icon: DollarSign, title: "Salary Expectations", desc: "Mention budget range if known" },
                        { icon: Users, title: "Company Details", desc: "Describe company culture, size, industry" },
                      ].map((tip, idx) => {
                        const Icon = tip.icon;
                        return (
                          <div key={idx} className="flex gap-2.5 p-2.5 rounded-md bg-muted/40 hover:bg-muted/60 transition-colors border border-transparent hover:border-border">
                            <Icon className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0 space-y-0.5">
                              <Text size="xs" weight="semibold" className="leading-tight">{tip.title}</Text>
                              <Text size="xs" variant="muted" className="leading-relaxed">{tip.desc}</Text>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="pt-3 border-t">
                      <div className="flex items-start justify-between gap-3 mb-2.5">
                        <div className="flex items-center gap-1.5">
                          <Lightbulb className="h-3.5 w-3.5 text-primary" />
                          <h4 className="text-sm font-semibold text-foreground">Example Prompt</h4>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleUseExample}
                          disabled={isGenerating}
                          className="gap-1.5 h-7 text-xs"
                        >
                          {copiedExample ? (
                            <>
                              <CheckCircle2 className="h-3 w-3" />
                              Used
                            </>
                          ) : (
                            <>
                              <Copy className="h-3 w-3" />
                              Use This
                            </>
                          )}
                        </Button>
                      </div>
                      <div className="p-3 bg-muted/40 rounded-md border-l-2 border-l-primary">
                        <Text variant="muted" className="text-xs leading-relaxed italic">
                          "{examplePrompt}"
                        </Text>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          </TabsContent>

          {/* AI Voice Agent Tab */}
          <TabsContent value="voice" className="space-y-6">
            {/* Audio Setup Modal */}
            {showInstructions && (
              <div 
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setShowInstructions(false);
                  }
                }}
              >
                <Card 
                  className="w-full max-w-[500px] mx-4 border shadow-lg relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  <CardHeader className="text-center space-y-2 pb-4 relative">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-4 right-4 h-8 w-8 rounded-full"
                      onClick={() => setShowInstructions(false)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center justify-center gap-2.5 mb-1">
                      <div className="p-1.5 rounded-md bg-primary/10">
                        <Mic className="h-5 w-5 text-primary" />
                      </div>
                      <CardTitle className="text-2xl font-semibold">Audio Setup</CardTitle>
                    </div>
                    <CardDescription>
                      Please test your microphone and speaker before starting.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Text size="sm" weight="medium" className="text-foreground">Microphone Test</Text>
                        {micStatus === "Tested" && (
                          <Badge variant="outline" className="gap-1 text-xs">
                            <CheckCircle2 className="h-3 w-3 text-primary" />
                            Ready
                          </Badge>
                        )}
                      </div>
                      <Button
                        onClick={handleMicTest}
                        variant="outline"
                        disabled={micStatus === "Testing"}
                        className={cn(
                          "w-full gap-2 transition-all",
                          micStatus === "Tested" && "border-primary/50 bg-primary/5"
                        )}
                        size="lg"
                      >
                        {micStatus === "Testing" ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Testing Microphone...
                          </>
                        ) : micStatus === "Tested" ? (
                          <>
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                            Microphone Ready
                          </>
                        ) : (
                          <>
                            <Mic className="h-4 w-4" />
                            Test Microphone
                          </>
                        )}
                      </Button>
                      {micMessage && (
                        <Alert variant={micStatus === "Error" ? "destructive" : "success"} className="py-2">
                          <AlertDescription className="text-sm">{micMessage}</AlertDescription>
                        </Alert>
                      )}
                    </div>
                    <div className="border-t border-border"></div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Text size="sm" weight="medium" className="text-foreground">Speaker Test</Text>
                        {speakerStatus === "Tested" && (
                          <Badge variant="outline" className="gap-1 text-xs">
                            <CheckCircle2 className="h-3 w-3 text-primary" />
                            Ready
                          </Badge>
                        )}
                      </div>
                      <Button
                        onClick={handleSpeakerTest}
                        variant="outline"
                        disabled={speakerStatus === "Testing"}
                        className={cn(
                          "w-full gap-2 transition-all",
                          speakerStatus === "Tested" && "border-primary/50 bg-primary/5"
                        )}
                        size="lg"
                      >
                        {speakerStatus === "Testing" ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Testing Speaker...
                          </>
                        ) : speakerStatus === "Tested" ? (
                          <>
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                            Speaker Ready
                          </>
                        ) : (
                          <>
                            <Volume2 className="h-4 w-4" />
                            Test Speaker
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                  <div className="px-6 pb-6">
                    <Button
                      onClick={handleStartVoiceFlow}
                      disabled={!(micStatus === "Tested" && speakerStatus === "Tested") || (balance < generateCost) || atJobLimit}
                      className="w-full gap-2"
                      size="lg"
                      style={{
                        backgroundColor: getColor('primary'),
                        color: getColor('primaryForeground'),
                        borderColor: getColor('primary')
                      }}
                    >
                      <Sparkles className="h-4 w-4" />
                      Start Voice Assistant
                      <span className="ml-2 text-xs text-gray-500">
                        -{generateCost} credits
                      </span>
                    </Button>
                  </div>
                </Card>
              </div>
            )}

            {/* AI Voice Agent Interface */}
            {!showInstructions && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <Card className="relative lg:col-span-2 border shadow-sm">
                  {isAIVoiceOn && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleCancelVoiceSession}
                      className="absolute right-4 top-3 z-10 h-8 w-8 rounded-full border-slate-300 bg-white text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-900"
                      aria-label="Cancel voice conversation"
                      title="Cancel voice conversation"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <CardHeader className="space-y-3 pb-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 rounded-md bg-primary/10">
                          <MessageSquare className="h-5 w-5 text-primary" />
                        </div>
                        <Heading level="h4">AI Voice Agent</Heading>
                      </div>
                      {isAIVoiceOn && (
                        <div className="mr-12 flex flex-shrink-0">
                          <div
                            className="flex h-[68px] w-[78px] flex-col items-center justify-center gap-1 rounded-xl bg-slate-500 px-2 text-white shadow-sm"
                            title={voiceStatusDetails.title}
                            aria-label={voiceStatusDetails.title}
                            role="status"
                            aria-live="polite"
                          >
                            <Sparkles className={cn("h-5 w-5", compactVoiceAnimation)} />
                            <span className="text-[10px] font-semibold leading-none">
                              {compactVoiceStatus}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2 pt-2">
                      <div className="flex items-center justify-between text-sm">
                        {/* <Text variant="muted" size="sm" className="font-medium">
                          Question {currentIndex + 1} of {questions.length - 1}
                        </Text> */}
                        <Text variant="muted" size="sm">
                          {isAIVoiceOn ? "Voice conversation in progress" : (() => {
                            const completedAnswers = answers.filter(a => a && a.trim() !== "" && a !== "No answer").length;
                            return completedAnswers > 0 ? `Creating JD...` : "Ready to start";
                          })()}
                        </Text>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {!isAIVoiceOn ? (
                      <div className="flex flex-col items-center justify-center space-y-6 py-12">
                        <div className="relative w-36 h-36 rounded-full bg-primary/10 flex items-center justify-center shadow-md border border-primary/20">
                          <Mic className="h-16 w-16 text-primary" />
                        </div>
                        <div className="text-center space-y-2">
                          <Heading level="h4">Ready to start?</Heading>
                          <Text variant="muted" className="max-w-md">
                            Click the button below to begin a conversation with our AI assistant. 
                            Speak naturally about your job requirements, and it will create a comprehensive job description.
                          </Text>
                        </div>
                        <Button
                          onClick={handleStartVoiceAI}
                          size="lg"
                          className="gap-2"
                          disabled={(balance < generateCost) || atJobLimit}
                          title={atJobLimit ? "You're at your plan's active job-post limit. Close a job or upgrade." : undefined}
                          style={{
                            backgroundColor: getColor('primary'),
                            color: getColor('primaryForeground'),
                            borderColor: getColor('primary')
                          }}
                        >
                          <Mic className="h-4 w-4" />
                          Start AI Voice Agent
                          <span className="ml-2 text-xs text-gray-500">
                            -{generateCost} credits
                          </span>
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-5 max-h-[400px] overflow-y-auto pr-2">
                            {conversation.map((msg) => (
                              <div key={msg.id} className={cn(
                                "flex items-start gap-3",
                                msg.role === 'user' ? "justify-end" : ""
                              )}>
                                {msg.role === 'assistant' && (
                                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                    <Sparkles className="h-5 w-5 text-primary" />
                                  </div>
                                )}

                                <div className="max-w-[75%]">
                                  <div className={cn(
                                    "px-4 py-3 rounded-2xl shadow-sm",
                                    msg.role === 'assistant'
                                      ? "bg-primary/5 border border-primary/20 rounded-tl-sm"
                                      : "bg-accent/5 border border-accent/30 rounded-tr-sm"
                                  )}>
                                    <Text size="sm">{msg.text}</Text>
                                  </div>
                                </div>

                                {msg.role === 'user' && (
                                  <div className="w-10 h-10 rounded-full flex items-center justify-center border">
                                    <Mic className="h-5 w-5 text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                            ))}
                            {liveTranscript && (
                              <div className="flex items-start gap-3 justify-end opacity-80">
                                <div className="max-w-[75%]">
                                  <div className="px-4 py-3 rounded-2xl border border-dashed bg-accent/5">
                                    <Text size="sm" className="italic text-muted-foreground">
                                      {liveTranscript}
                                    </Text>
                                  </div>
                                </div>

                                <div className="w-10 h-10 rounded-full flex items-center justify-center border">
                                  <Mic className="h-5 w-5 text-muted-foreground" />
                                </div>
                              </div>
                            )}

                            <div ref={chatEndRef} />
                          </div>

                      </>
                    )}
                  </CardContent>
                </Card>
                <Collapsible open={tipsOpen} onOpenChange={setTipsOpen}>
                  <Card className="border border-primary/20 bg-background shadow-sm sticky top-4">
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer transition-all duration-200 bg-primary/5 hover:bg-primary/10 border-b border-border">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/20 shadow-sm ring-1 ring-primary/20">
                              <Lightbulb className="h-5 w-5 text-primary" />
                            </div>
                            <div className="text-left">
                              <CardTitle className="text-base font-semibold text-foreground">Quick Tips</CardTitle>
                              <CardDescription className="text-xs mt-0.5">
                                {tipsOpen ? "Click to collapse" : "Click to expand helpful tips"}
                              </CardDescription>
                            </div>
                          </div>
                          <ChevronDown className={cn(
                            "h-4 w-4 text-muted-foreground transition-transform duration-200",
                            tipsOpen && "transform rotate-180"
                          )} />
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-3 pb-3">
                        <div className="space-y-1.5">
                          {[
                            { icon: Volume2, title: "Speak Clearly", text: "Speak naturally and clearly" },
                            { icon: MessageSquare, title: "Be Specific", text: "Use specific titles and experience levels" },
                            { icon: Sparkles, title: "Include Details", text: "Mention salary, location, and key skills" },
                            { icon: CheckCircle2, title: "Keep it Concise", text: "Short, clear phrases work best" },
                            { icon: CheckCircle2, title: "You Can Edit", text: "Review and edit the JD at the end" }
                          ].map((tip, idx) => {
                            const Icon = tip.icon;
                            return (
                              <div key={idx} className="group relative flex gap-2 p-2 rounded-md transition-all duration-200 bg-muted/30 border border-border/50 hover:border-primary/30 hover:shadow-sm hover:bg-primary/5">
                                <div className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center bg-primary/10 border border-primary/20">
                                  <Icon className="h-3.5 w-3.5 text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <Text size="xs" weight="semibold" className="leading-tight text-foreground group-hover:text-primary transition-colors mb-0.5">
                                    {tip.title}
                                  </Text>
                                  <Text size="xs" variant="muted" className="leading-tight">
                                    {tip.text}
                                  </Text>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              </div>
            )}
          </TabsContent>

          {/* Create Tab */}
          <TabsContent value="create" className="space-y-6">
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-card to-card">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center justify-center space-y-6 py-12 text-center">
                  <div className="relative w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center shadow-md border border-primary/20">
                    <FileEdit className="h-12 w-12 text-primary" />
                  </div>
                  <div className="space-y-2 max-w-md">
                    <Heading level="h4">Create Job Post Manually</Heading>
                    <Text variant="muted" className="leading-relaxed">
                      Start with a clean form to craft every detail of your job posting. 
                      Perfect for when you want full control over the content and structure.
                    </Text>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={() => navigate('/hirewithus/postjob/create')}
                      size="lg"
                      className="gap-2 min-w-[200px]"
                      style={{
                        backgroundColor: getColor('primary'),
                        color: getColor('primaryForeground'),
                        borderColor: getColor('primary')
                      }}
                    >
                      <FileEdit className="h-4 w-4" />
                      Start Creating
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 w-full max-w-2xl">
                    {[
                      { icon: Briefcase, title: "Full Control", desc: "Craft every detail exactly as you want" },
                      { icon: Zap, title: "Step-by-Step", desc: "Guided form with multiple steps" },
                      { icon: CheckCircle2, title: "Review & Edit", desc: "Review before publishing" }
                    ].map((feature, idx) => {
                      const Icon = feature.icon;
                      return (
                        <div key={idx} className="flex flex-col items-center gap-2 p-4 rounded-lg bg-muted/40 border border-border/50">
                          <Icon className="h-5 w-5 text-primary" />
                          <Text size="sm" weight="semibold">{feature.title}</Text>
                          <Text size="xs" variant="muted" className="text-center">{feature.desc}</Text>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Upload Tab */}
          <TabsContent value="upload" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-card to-card">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 rounded-md bg-primary/10">
                      <Upload className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-base font-semibold">Upload Your File</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {uploadError && (
                    <Alert variant="destructive">
                      <AlertTitle>Error</AlertTitle>
                      <AlertDescription>{uploadError}</AlertDescription>
                    </Alert>
                  )}

                  {showSuccess && (
                    <Alert variant="success" className="flex items-start gap-3 p-4">
                      <CheckCircle2 className="h-5 w-5 mt-0.5 text-primary" />
                      <div>
                        <AlertTitle>Success</AlertTitle>
                        <AlertDescription>
                          Job Description uploaded and processed successfully!
                        </AlertDescription>
                      </div>
                    </Alert>
                  )}

                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={cn(
                      "border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors",
                      isDragging
                        ? "border-primary bg-primary/5"
                        : "border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/50",
                      selectedFile && "border-primary bg-primary/5"
                    )}
                  >
                    <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                    <Text size="lg" weight="semibold" className="mb-2">
                      Drag & drop files or click to browse
                    </Text>
                    <Text size="sm" variant="muted">
                      Supported formats: PDF, DOC, DOCX, TXT (Max 10MB)
                    </Text>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx,.txt"
                      disabled={isUploading}
                    />
                  </div>

                  {selectedFile && !isUploading && (
                    <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                      <FileText className="h-5 w-5 text-primary" />
                      <div className="flex-1 min-w-0">
                        <Text size="sm" weight="medium" className="truncate">
                          {selectedFile.name}
                        </Text>
                        <Text size="xs" variant="muted">
                          {(selectedFile.size / 1024).toFixed(2)} KB
                        </Text>
                      </div>
                    </div>
                  )}

                  {isUploading && (
                    <div className="space-y-2">
                      <Progress value={uploadProgress} />
                      <Text size="sm" variant="muted" className="text-center">
                        Uploading and processing... {uploadProgress}%
                      </Text>
                    </div>
                  )}
                </CardContent>
                <CardFooter>
                  <Button
                    variant="default"
                    onClick={handleUpload}
                    disabled={isUploading || !selectedFile || (balance < generateCost) || atJobLimit}
                    title={atJobLimit ? "You're at your plan's active job-post limit. Close a job or upgrade." : undefined}
                    className="w-full"
                    size="lg"
                    style={{
                      backgroundColor: getColor('primary'),
                      color: getColor('primaryForeground'),
                      borderColor: getColor('primary')
                    }}
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Upload JD
                        <span className="ml-2 text-xs text-gray-500">
                          -{generateCost} credits
                        </span>
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>

              <Card className="border border-primary/20 bg-background shadow-sm">
                <CardHeader>
                  <div className="flex items-center gap-2.5">
                    <div className="p-1.5 rounded-md bg-primary/10">
                      <Lightbulb className="h-4 w-4 text-primary" />
                    </div>
                    <CardTitle className="text-base font-semibold">How It Works</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {[
                    { step: 1, title: "Upload Your Document", desc: "Select a job description file in PDF, DOC, DOCX, or TXT format" },
                    { step: 2, title: "AI Processing", desc: "Our AI extracts and structures all relevant job details automatically" },
                    { step: 3, title: "Review & Post", desc: "Review the generated job posting and publish it to attract candidates" }
                  ].map((item) => (
                    <div key={item.step} className="flex gap-4">
                      <div 
                        className="flex-shrink-0 w-10 h-10 rounded-full text-white flex items-center justify-center font-bold text-base shadow-md"
                        style={{
                          backgroundColor: getColor('primary'),
                          color: getColor('primaryForeground'),
                        }}
                      >
                        {item.step}
                      </div>
                      <div className="flex-1">
                        <Heading level="h3" className="text-lg font-semibold mb-1">
                          {item.title}
                        </Heading>
                        <Text size="sm" variant="muted">
                          {item.desc}
                        </Text>
                      </div>
                    </div>
                  ))}
                </CardContent>
                <CardFooter>
                  <Text size="sm" variant="muted" className="text-center w-full">
                    ✨ Our AI will transform your document into a structured job posting in seconds!
                  </Text>
                </CardFooter>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </EmployerLayout>
  );
};

export default PostJobLanding;
